import { useEffect, useRef } from "react";

/**
 * How long the global search modal takes to fade out, in milliseconds. Shared
 * with the modal's exit transition in GlobalSearchAppBar.
 */
export const SEARCH_MODAL_EXIT_DURATION_MS = 150;

/**
 * When the search field is emptied after a close — a little after the modal has
 * left the screen, since emptying the query while it is still visible swaps the
 * results back to the initial view mid-fade.
 */
export const CLEAR_SEARCH_DELAY_MS = SEARCH_MODAL_EXIT_DURATION_MS + 50;

interface UseClearSearchAfterCloseArgs {
  isSearchbarOpen: boolean;
  /** Empties the search field — the input value and the search query it holds. */
  clearSearch: () => void;
}

/**
 * Empties the search field once the global search modal has closed, so a
 * finished search is not left behind in the header search field.
 *
 * Reopening the search within the delay keeps the query, which is what opening
 * a recent search from the dashboard relies on. Belongs in a provider that
 * outlives the page: opening a result navigates, and a page-level owner would
 * unmount and cancel the pending clear.
 */
export const useClearSearchAfterClose = ({
  isSearchbarOpen,
  clearSearch,
}: UseClearSearchAfterCloseArgs) => {
  const wasSearchbarOpenRef = useRef(isSearchbarOpen);
  // Held in a ref so a re-render with a new callback does not restart the timer.
  const clearSearchRef = useRef(clearSearch);
  clearSearchRef.current = clearSearch;

  useEffect(() => {
    const justClosed = wasSearchbarOpenRef.current && !isSearchbarOpen;
    wasSearchbarOpenRef.current = isSearchbarOpen;

    if (!justClosed) {
      return;
    }

    const timeout = setTimeout(() => {
      clearSearchRef.current();
    }, CLEAR_SEARCH_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [isSearchbarOpen]);
};
